import { getJWTToken } from '../../utils/auth';
import type { SisMapping, SisMappingStorage } from '../Upload/sisFieldMapping';
import { authHeaders, authHeadersFor, getImpersonation, type ImpersonationTarget } from '../../core/impersonation';

const apiUrl = import.meta.env.REACT_APP_BAMBOO_API_URL ?? '';

export type SavedSisMappingEntry = { headers: string[]; mapping: SisMapping };
export type SisMappingSet = { latest: string; bySignature: Record<string, SavedSisMappingEntry> };

export async function fetchSisMappings(): Promise<SisMappingSet> {
  const token = await getJWTToken();
  const response = await fetch(`${apiUrl}/uploads/sis-mapping`, {
    headers: authHeaders(token),
  });
  if (!response.ok) {
    throw new Error(`fetch-sis-mappings failed: ${response.status}`);
  }
  const body = (await response.json()) as Partial<SisMappingSet> | null;
  return { latest: body?.latest ?? '', bySignature: body?.bySignature ?? {} };
}

export async function putSisMapping(
  signature: string,
  headers: string[],
  mapping: SisMapping,
  sessionToken?: string,
  target?: ImpersonationTarget | null,
): Promise<void> {
  const token = sessionToken ?? (await getJWTToken());
  const requestHeaders = target === undefined ? authHeaders(token) : authHeadersFor(token, target);
  const response = await fetch(`${apiUrl}/uploads/sis-mapping`, {
    method: 'PUT',
    headers: { ...requestHeaders, 'Content-Type': 'application/json' },
    body: JSON.stringify({ signature, headers, mapping }),
  });
  if (!response.ok) {
    throw new Error(`save-sis-mapping failed: ${response.status}`);
  }
}

let cached: SisMappingSet | null = null;
let hydrateSeq = 0;
let sessionEpoch = 0;
const pendingSaves = new Map<string, SavedSisMappingEntry>();
const failedSaves = new Set<string>();
let pendingLatest = '';
let putChain: Promise<void> = Promise.resolve();

export const sisMappingCache: SisMappingStorage = {
  getItem: () => (cached ? JSON.stringify(cached) : null),
  setItem: (_key: string, value: string) => {
    let parsed: SisMappingSet;
    try {
      parsed = JSON.parse(value) as SisMappingSet;
    } catch {
      return;
    }
    if (!parsed || typeof parsed !== 'object' || !parsed.bySignature) return;
    cached = parsed;
    hydrateSeq += 1;
    const entry = parsed.bySignature[parsed.latest];
    if (!entry || Object.keys(entry.mapping).length === 0) return;
    const signature = parsed.latest;
    pendingSaves.set(signature, entry);
    pendingLatest = signature;
    enqueuePut(signature, entry, sessionEpoch);
  },
};

function enqueuePut(signature: string, entry: SavedSisMappingEntry, epoch: number): void {
  failedSaves.delete(signature);
  const target = getImpersonation();
  const sessionToken = getJWTToken();
  sessionToken.catch(() => {});
  putChain = putChain.then(async () => {
    if (epoch !== sessionEpoch) return;
    try {
      const token = await sessionToken;
      if (epoch !== sessionEpoch) return;
      await putSisMapping(signature, entry.headers, entry.mapping, token, target);
      if (epoch !== sessionEpoch) return;
      if (pendingSaves.get(signature) === entry) {
        pendingSaves.delete(signature);
        if (pendingLatest === signature) pendingLatest = '';
      }
      hydrateSeq += 1;
    } catch (err) {
      if (epoch === sessionEpoch && pendingSaves.get(signature) === entry) {
        failedSaves.add(signature);
      }
      console.warn('sis-mapping save failed; keeping it pending locally', err);
    }
  });
}

export async function sisMappingSaveSettled(signature: string): Promise<boolean> {
  await putChain;
  return !failedSaves.has(signature);
}

export function retrySisMappingSaves(): void {
  for (const [signature, entry] of pendingSaves) {
    if (failedSaves.has(signature)) enqueuePut(signature, entry, sessionEpoch);
  }
}

export async function sisMappingSavesSettled(): Promise<boolean> {
  await putChain;
  return failedSaves.size === 0;
}

let inFlightHydration: { seq: number; promise: Promise<void> } | null = null;

export function hydrateSisMappings(): Promise<void> {
  if (inFlightHydration && inFlightHydration.seq === hydrateSeq) {
    return inFlightHydration.promise;
  }
  const seq = ++hydrateSeq;
  const promise = (async () => {
    try {
      const set = await fetchSisMappings();
      if (seq !== hydrateSeq) return;
      cached = overlayPendingSaves(set);
      for (const [signature, entry] of pendingSaves) {
        if (failedSaves.has(signature)) enqueuePut(signature, entry, sessionEpoch);
      }
    } catch (err) {
      if (seq !== hydrateSeq) return;
      console.warn('sis-mapping hydrate failed; saved mappings unavailable this session', err);
    } finally {
      if (inFlightHydration?.seq === seq) inFlightHydration = null;
    }
  })();
  inFlightHydration = { seq, promise };
  return promise;
}

function overlayPendingSaves(server: SisMappingSet): SisMappingSet {
  if (pendingSaves.size === 0) return server;
  const bySignature = { ...server.bySignature };
  for (const [signature, entry] of pendingSaves) {
    bySignature[signature] = entry;
  }
  return { latest: pendingLatest || server.latest, bySignature };
}

export function resetSisMappingSession(): void {
  cached = null;
  hydrateSeq += 1;
  sessionEpoch += 1;
  pendingSaves.clear();
  failedSaves.clear();
  pendingLatest = '';
  putChain = Promise.resolve();
}
